/**
 * Agent Branding Configuration
 * 
 * Single source of truth for all agent identity, naming, and visual branding.
 * Update this file to white-label the AI agent for any product.
 */

export interface AgentBrandingConfig {
  /** Display name shown in chat header and tooltips */
  name: string;
  /** Full name with description (used in init animation) */
  fullName: string;
  /** Short acronym for avatar fallback */
  avatarFallback: string;
  /** Desktop avatar image path */
  avatarSrc: string;
  /** Mobile/touch avatar image path */
  avatarSrcMobile: string;
  /** Chat bubble button image (frame-of-motion / Cortex branding) */
  chatBubbleSrc: string;
  /** Welcome message shown when chat opens */
  welcomeMessage: string;
  /** Loading steps shown during initialization */
  loadingSteps: string[];
  /** Tooltip text for the chat bubble button */
  chatBubbleTooltip: string;
  /** Aria label for accessibility */
  chatBubbleAriaLabel: string;
  /** Toast notification for first-time visitors */
  introToast: {
    title: string;
    description: string;
    actionLabel: string;
  };
  /** Account creation prompt text */
  accountPrompt: {
    unlockTitle: string;
    unlockSubtitle: string;
    compactLabel: string;
    benefitConversations: string;
    benefitTodoLists: string;
    benefitAdvanced: string;
  };
  /** Action items list title */
  actionItemsTitle: string;
  /** Thinking animation steps */
  thinkingSteps: string[];
}

export const agentBranding: AgentBrandingConfig = {
  name: 'Cortex',
  fullName: 'CORTEX',
  avatarFallback: 'AI',
  avatarSrc: '/flowstate-logo.png',
  avatarSrcMobile: '/lovable-uploads/flowstate-dna-clockwork.svg',
  chatBubbleSrc: '/lovable-uploads/flowstate-dna-clockwork.svg',
  welcomeMessage: `👋 Hi! I'm **Cortex** — your always-on AI teammate for automation, logistics, and intelligence. I work in the background 24/7 so you don't have to.\n\n🚀 **Get started instantly** — click the ⚡ Quick Actions button to:\n• Create tasks & manage your workflow\n• Schedule meetings & events\n• Generate reports & insights\n• Automate repetitive work\n\nOr just type what you need — I'm already on it.`,
  loadingSteps: [
    'Loading conversations...',
    'Mycelium protocol...',
    'Initializing AI...',
    'Syncing with edge functions...',
    'Ready!',
  ],
  chatBubbleTooltip: 'Your Always-On AI Teammate',
  chatBubbleAriaLabel: 'Open AI Teammate',
  introToast: {
    title: 'Meet Cortex — Your AI Teammate',
    description: 'Always-on automation, intelligence, and insights working in the background',
    actionLabel: 'Say hello',
  },
  accountPrompt: {
    unlockTitle: 'Unlock the Full AI Experience',
    unlockSubtitle: 'Create your free account in just 30 seconds',
    compactLabel: 'Get the full AI experience',
    benefitConversations: 'Your chat history is saved and accessible across devices',
    benefitTodoLists: 'Create and manage todo lists from your conversations',
    benefitAdvanced: 'Access visual aids, charts, and enhanced AI capabilities',
  },
  actionItemsTitle: '✅ Action Items',
  thinkingSteps: [
    'Neural link active',
    'Mycelium sync',
    'Deep processing',
    'Cross-referencing',
    'Compiling response',
  ],
};
